import { useEffect, useMemo, useRef, useState } from 'react';

import { produce } from 'immer';
import { cloneDeep, isEqual } from 'lodash';
import { MapRef } from 'react-map-gl/mapbox';
import { useBlocker } from 'react-router';

import Page from 'components/Page/Page';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import useGeoConfig, {
  DEFAULT_GEO_CONFIG,
  PostGeoConfig,
  PostGeoConfigLocation,
} from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import ConfigProfilesSidebar from 'pages/Settings/GeoConfigSettings/ConfigProfilesSidebar';
import GeoConfigHeader from 'pages/Settings/GeoConfigSettings/GeoConfigHeader';
import GeoMap from 'pages/Settings/GeoConfigSettings/GeoMap';
import NewProfileSidebar from 'pages/Settings/GeoConfigSettings/NewProfileSidebar';
import UnsavedChanges from 'pages/Settings/GeoConfigSettings/UnsavedChanges';
import {
  GeoConfigProvider,
  useGeoConfigContext,
} from 'pages/Settings/GeoConfigSettings/utils/GeoConfigContext';
import Modal from '@lotlinx-aws/sparky-component-library/Sparky/Modal';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import { FeatureFlag } from 'types/FeatureFlags';

import styles from './GeoConfigSettings.module.scss';

/**
 * GeoConfigSettings
 */
export default function GeoConfigSettings() {
  const [dealerId] = useSelectedDealerId();

  return (
    <GeoConfigProvider dealerId={dealerId}>
      <GeoConfigSettingsContent />
    </GeoConfigProvider>
  );
}

export function GeoConfigSettingsContent() {
  // toggle profile detail state
  const [isProfileDetailOpen, setIsProfileDetailOpen] = useState<boolean>(false);
  const {
    tempGeoConfigs,
    initialGeoConfig,
    setEditingPin,
    setTempGeoConfigs,
    creatingNewProfile,
    selectProfile,
    setLastSavedTempGeoConfig,
    setInitialGeoConfig,
    setSelectProfile,
    setSelectPin,
    setCreatingNewProfile,
  } = useGeoConfigContext();

  const { data, error, refetch } = useGeoConfig();

  const defaultGeoConfig: PostGeoConfig | undefined = data
    ? {
        // created profile configId is an integer
        configId: DEFAULT_GEO_CONFIG.configId,
        name: DEFAULT_GEO_CONFIG.name,
        locations: [
          {
            id: 1,
            name: 'Pin 1',
            latitude: data.defaults.latitude,
            longitude: data.defaults.longitude,
            radius: data.defaults.radius,
          },
        ],
      }
    : undefined;

  const mapRef = useRef<MapRef | undefined>(undefined);

  const isGeoConfigChanged = useMemo(
    () => !isEqual(tempGeoConfigs, initialGeoConfig),
    [tempGeoConfigs, initialGeoConfig],
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return isGeoConfigChanged && currentLocation.pathname !== nextLocation.pathname;
  });

  const onStay = () => blocker.reset && blocker.reset();

  const onLeave = () => blocker.proceed && blocker.proceed();

  const onSelectPin = (location: PostGeoConfigLocation) => {
    const { longitude, latitude } = location;
    mapRef.current?.flyTo({ center: [longitude, latitude], duration: 2000 });
  };

  const onEditing = (location: PostGeoConfigLocation) => {
    setEditingPin(location);
  };

  const onCreateNewProfile = (profile: PostGeoConfig) => {
    let newProfile: PostGeoConfig = cloneDeep(profile);
    // update the lat and lng to the current map center
    newProfile.locations[0].latitude = defaultGeoConfig?.locations[0].latitude ?? 0;
    newProfile.locations[0].longitude = defaultGeoConfig?.locations[0].longitude ?? 0;
    newProfile.locations[0].radius = defaultGeoConfig?.locations[0].radius ?? 30;

    onSelectPin(newProfile.locations[0]);
    setCreatingNewProfile(true);
    setIsProfileDetailOpen(false);
    setLastSavedTempGeoConfig(initialGeoConfig);
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        draft.push(newProfile);
        return draft;
      }),
    );
    setSelectProfile(newProfile);
    setEditingPin(newProfile.locations[0]);
    setSelectPin(newProfile.locations[0]);
  };

  const onCloseCreateNewProfile = () => {
    setCreatingNewProfile(false);
    onSelectPin(initialGeoConfig[0].locations[0]);
    setTempGeoConfigs(initialGeoConfig);
    setSelectPin(undefined);
    setEditingPin(undefined);
    setSelectProfile(initialGeoConfig[0]);
  };

  const onSaveCreateNewProfile = () => {
    selectProfile && onSelectPin(selectProfile.locations[0]);
    setSelectPin(undefined);
    setEditingPin(undefined);
  };

  useEffect(() => {
    if (data && defaultGeoConfig) {
      setTempGeoConfigs([defaultGeoConfig, ...data.geoConfigs]);
      setInitialGeoConfig([defaultGeoConfig, ...data.geoConfigs]);
      const findProfile = [defaultGeoConfig, ...data.geoConfigs].find(
        (profile) =>
          profile.configId === selectProfile?.configId || profile.name === selectProfile?.name,
      );
      if (!selectProfile || !findProfile) {
        setSelectProfile(defaultGeoConfig);
      } else {
        setSelectProfile(findProfile);
      }
    }
  }, [data]);

  if (error) {
    return (
      <div className={styles.geoConfigSettings}>
        <GeoConfigHeader />
        <div className={styles.geoConfigContent}>
          <Text size='14' fontWeight={7}>
            Something went wrong, try refreshing the page.
          </Text>
          <Text size='12'>Failed to fetch geo configuration data</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.geoConfigSettings}>
      <GeoConfigHeader />
      {!creatingNewProfile ? (
        <ConfigProfilesSidebar
          isGeoConfigChanged={isGeoConfigChanged}
          onSelectPin={onSelectPin}
          onCreateNewProfile={onCreateNewProfile}
        />
      ) : (
        <NewProfileSidebar
          refetch={refetch}
          defaultGeoConfig={defaultGeoConfig}
          onSelectPin={onSelectPin}
          onCloseCreateNewProfile={onCloseCreateNewProfile}
          onSaveCreateNewProfile={onSaveCreateNewProfile}
        />
      )}
      <GeoMap
        mapRef={mapRef}
        refetch={refetch}
        defaultGeoConfig={defaultGeoConfig}
        isProfileDetailOpen={isProfileDetailOpen}
        setIsProfileDetailOpen={setIsProfileDetailOpen}
        onEditing={onEditing}
        onSelectPin={onSelectPin}
      />
      <Modal isOpen={blocker.state === 'blocked'} aria-label='Unsaved change modal'>
        <UnsavedChanges onLeave={onLeave} onStay={onStay} />
      </Modal>
    </div>
  );
}

export function Component() {
  return (
    <Page requireDealer title='Geo Configuration' flag={FeatureFlag.ENABLE_GEO_CONFIG_SETTING}>
      <GeoConfigSettings />
    </Page>
  );
}
